import { BlockedTime, BlockedTimeResult } from '../models';
import { msFromMidnightToTimestamp } from './time-of-day';

/**
 * Blocks a reservation based on its START time, not on duration overlap.
 * A reservation is blocked only when its quoted (start) time falls within a
 * window: windowStart <= quotedTime < windowEnd (end exclusive).
 */
export function isTimeBlocked(
  quotedTime: number,
  timezone: string,
  blockedTimes: BlockedTime[],
  serviceIds: string[] = [],
  resourceIds: string[] = [],
): BlockedTimeResult {
  for (const blockedTime of blockedTimes) {
    if (!isWithinWindow(quotedTime, timezone, blockedTime)) {
      continue;
    }

    if (!matchesService(blockedTime, serviceIds)) {
      continue;
    }

    if (!matchesResource(blockedTime, resourceIds)) {
      continue;
    }

    return {
      blocked: true,
      reason: blockedTime.reason,
      blockedTime,
    };
  }

  return { blocked: false };
}

function isWithinWindow(
  quotedTime: number,
  timezone: string,
  blockedTime: BlockedTime,
): boolean {
  const windowStart = msFromMidnightToTimestamp(
    blockedTime.date,
    timezone,
    blockedTime.startTime,
  );
  const windowEnd = msFromMidnightToTimestamp(
    blockedTime.date,
    timezone,
    blockedTime.endTime,
  );

  return quotedTime >= windowStart && quotedTime < windowEnd;
}

function matchesService(blockedTime: BlockedTime, serviceIds: string[]): boolean {
  if (blockedTime.allServices) {
    return true;
  }

  if (!serviceIds.length) {
    return false;
  }

  return blockedTime.services.some(serviceId => serviceIds.includes(serviceId));
}

function matchesResource(blockedTime: BlockedTime, resourceIds: string[]): boolean {
  if (!blockedTime.resources?.length) {
    return true;
  }

  return blockedTime.resources.some(resourceId => resourceIds.includes(resourceId));
}
